//@ts-check
import { InputStream } from '../lmvtk/common/InputStream.js';
import { MIN_LEVELS_IN_LOD0, RCS_MAX_OCTREE_LEVELS } from './RcsConstants.js';
import { calculateLevelsToProcessForLod } from './RcsLodMapping.js';

/**
 * Reads a 64-bit unsigned integer from two consecutive 32-bit little-endian words.
 * The result is a JavaScript number, which can represent integers up to 2^53 safely.
 * @param {InputStream} stream
 * @returns {number}
 */
function readUint64(stream) {
  const low = stream.getUint32();
  const high = stream.getUint32();
  return low + high * 0x100000000;
}

/**
 * @typedef {import('./RcsExtractorCore').RcsHeader} RcsHeader
 * @typedef {import('./RcsExtractorCore').RcsChunk} RcsChunk
 * @typedef {import('./RcsExtractorCore').UmbrellaLeafNode} UmbrellaLeafNode
 * @typedef {import('./RcsFileLoader').OctreeNode} OctreeNode
 * @typedef {import('./RcsFileLoader').RcsOctreeStructure} RcsOctreeStructure
 */

// ── Type Definitions ─────────────────────────────────────────────────

/**
 * A single umbrella intermediate (interior) node from chunk 3.
 * @typedef {Object} UmbrellaIntermediateNode
 * @property {number}   index            - Index within the intermediate node array
 * @property {number[]} rgba             - [r, g, b, a] vertex colour (0–255)
 * @property {number}   normalIndex      - Compressed normal index (14 bits)
 * @property {number}   childInfo        - Bitmask of occupied octree children (8 bits)
 * @property {number[]} svoBounds        - Axis-aligned bounding box [minX, minY, minZ, maxX, maxY, maxZ]
 * @property {number[]} misc             - 4 flag bytes [m0, m1, m2, m3]
 * @property {number}   indexIntoLeafArray - Index into the umbrella leaf array (−1 for interior nodes)
 * @property {number}   firstChildIndex  - Offset to first child within the next level's node range (−1 for leaves)
 */

/**
 * Extended umbrella data parsed from chunk 3.
 * @typedef {Object} UmbrellaExtendedData
 * @property {number[]}                   levelNodeCounts       - Number of intermediate nodes per octree level (length 32)
 * @property {UmbrellaIntermediateNode[]} intermediateNodes      - All intermediate nodes across all levels
 * @property {number[]}                   nodeLevelStartIndices  - Cumulative start indices per level (length 33)
 */

/**
 * Transform data describing the SVO-local → world translation.
 * @typedef {Object} SvoTransformData
 * @property {number}  offsetX - X component of the SVO bounds origin
 * @property {number}  offsetY - Y component of the SVO bounds origin
 * @property {number}  offsetZ - Z component of the SVO bounds origin
 * @property {boolean} [isIdentityRotation] - Always true for RCS (no rotation)
 */

/**
 * Result of {@link RcsParser.decodeSvoPointsDirect}.
 * @typedef {Object} SvoDecodeResult
 * @property {Float32Array}     interleavedData - Interleaved XYZRGB (6 floats per point)
 * @property {number}           count           - Number of decoded points
 * @property {SvoTransformData} transformData   - SVO → world translation
 * @property {number[]}         bounds          - SVO bounding box [minX, minY, minZ, maxX, maxY, maxZ]
 * @property {number[]}         pointsPerLevel  - Surviving (non-deleted) point count per octree level, indexed by level
 * @property {UmbrellaLeafNode} [leafNode]      - Echoed back when count is 0
 */

/**
 * Per-level point data entry returned by {@link RcsParser.decodeSvoPointsByLevel}.
 * @typedef {Object} SvoLevelEntry
 * @property {Float32Array} interleavedData - Interleaved XYZRGB for this level
 * @property {number}       count           - Number of points at this level
 * @property {number}       level           - Octree level index
 */

/**
 * Result of {@link RcsParser.decodeSvoPointsByLevel}.
 * @typedef {Object} SvoByLevelResult
 * @property {SvoLevelEntry[]}       pointsByLevel - One entry per octree level that contained points
 * @property {SvoTransformData|null} transformData - SVO → world translation
 * @property {number[]}              [bounds]      - SVO bounding box
 */

/**
 * Node and leaf index ranges for a set of octree levels.
 * @typedef {Object} SvoNodeRanges
 * @property {number} nodeStart - First interior-node index to process
 * @property {number} nodeEnd   - One-past-last interior-node index
 * @property {number} leafStart - First leaf-node index to process
 * @property {number} leafEnd   - One-past-last leaf-node index
 * @property {number} [minLevel] - Lowest octree level in the range
 * @property {number} [maxLevel] - Highest octree level in the range
 */

/**
 * Result of the internal level-calculation helper.
 * @typedef {Object} LevelProcessingInfo
 * @property {number[]}      levelsToProcess  - Octree levels that fall within the requested point range
 * @property {number}        lastIncludedLevel - Highest level that still contributes points
 * @property {number}        estimatedPoints   - Estimated total point count
 * @property {SvoNodeRanges} nodeRanges        - Interior-node and leaf-node index ranges
 */


// ── Standalone helpers ───────────────────────────────────────────────

/**
 * Extract occupied child indices from an 8-bit child-info bitmask.
 * @param {number} childInfo - Bitmask (bits 0–7 correspond to octree children 0–7)
 * @returns {number[]} Sorted list of occupied child indices
 */
export function getChildrenFromMask(childInfo) {
  const children = [];
  for (let i = 0; i < 8; i++) {
    if (childInfo & 1 << i) {
      children.push(i);
    }
  }
  return children;
}

/**
 * RCS Parser — stateless helpers for decoding all binary structures
 * within an RCS point-cloud file.
 */
export class RcsParser {

  /**
   * Parse the 98 605-byte RCS file header.
   * @param {Uint8Array} headerData - Raw header bytes
   * @returns {RcsHeader}
   */
  static parseHeader(headerData) {
    const stream = new InputStream(headerData);

    // Magic word (8 bytes)
    const magic = stream.getString(8).replace(/\0/g, '');

    // Version (8 bytes: 2 x uint32)
    const majorVersion = stream.getUint32();
    const minorVersion = stream.getUint32();

    // Translation, rotation, scale (each 24 bytes: 3 x double)
    const translation = [stream.getFloat64(), stream.getFloat64(), stream.getFloat64()];
    const rotation = [stream.getFloat64(), stream.getFloat64(), stream.getFloat64()];
    const scale = [stream.getFloat64(), stream.getFloat64(), stream.getFloat64()];

    // Scanner origin (24 bytes: 3 x double)
    const scannerOrigin = [stream.getFloat64(), stream.getFloat64(), stream.getFloat64()];

    // SVO bounds (48 bytes: RCBox = 6 x double)
    const svoBounds = [
    stream.getFloat64(), stream.getFloat64(), stream.getFloat64(),
    stream.getFloat64(), stream.getFloat64(), stream.getFloat64()];

    // Scan bounds (48 bytes: RCBox = 6 x double)
    const scanBounds = [
    stream.getFloat64(), stream.getFloat64(), stream.getFloat64(),
    stream.getFloat64(), stream.getFloat64(), stream.getFloat64()];


    // Octree flags (4 bytes)
    const octreeFlags = stream.getUint32();
    // Provider (4 bytes)
    const provider = stream.getUint32();

    // Total points (8 bytes: uint64 read as two uint32s)
    const totalPoints = readUint64(stream);

    // Boolean flags (3 bytes)
    const hasRGB = stream.getUint8() !== 0;
    const hasNormals = stream.getUint8() !== 0;
    const hasIntensity = stream.getUint8() !== 0;

    // Scan ID (38 bytes)
    const scanId = stream.getString(38).replace(/\0/g, '');
    // Coordinate system (32 bytes)
    const coordinateSystem = stream.getString(32).replace(/\0/g, '');
    // Number of file chunks (4 bytes)
    const numFileChunks = stream.getUint32();

    return {
      magic, majorVersion, minorVersion,
      translation, rotation, scale, scannerOrigin,
      svoBounds, scanBounds,
      octreeFlags, provider, totalPoints,
      hasRGB, hasNormals, hasIntensity,
      scanId, coordinateSystem, numFileChunks,
      chunkDirectoryOffset: stream.offset
    };
  }

  /**
   * Parse the chunk directory from the RCS header buffer.
   * @param {Uint8Array} headerData - Buffer containing the file header and chunk directory data
   * @param {number} directoryOffset - Byte offset within `headerData`
   * @param {number} numChunks - Number of directory entries
   * @returns {RcsChunk[]}
   */
  static parseChunkDirectory(headerData, directoryOffset, numChunks) {
    const stream = new InputStream(headerData);
    stream.seek(directoryOffset);

    /** @type {RcsChunk[]} */
    const chunks = [];
    for (let i = 0; i < numChunks; i++) {
      const chunkId = stream.getUint32();
      stream.skip(4); // Skip padding

      const fileOffsetStart = readUint64(stream);
      const sizeInBytes = readUint64(stream);

      chunks.push({
        chunkId,
        fileOffsetStart,
        sizeInBytes,
        name: RcsParser.getChunkName(chunkId)
      });
    }

    return chunks;
  }

  /**
   * Parse umbrella octree leaf data (chunk 2).
   * @param {Uint8Array} chunkData - Raw chunk bytes
   * @returns {UmbrellaLeafNode[]}
   */
  static parseUmbrellaOctreeData(chunkData) {
    const stream = new InputStream(chunkData);

    // Read numLeafs (8 bytes: uint64)
    const numLeafs = readUint64(stream);

    console.log(`Reading ${numLeafs} umbrella leaf nodes...`);
    console.log(`Buffer size: ${chunkData.length}, Expected size: ${8 + numLeafs * 368}`);

    /** @type {UmbrellaLeafNode[]} */
    const leafNodes = [];

    for (let i = 0; i < numLeafs; i++) {
      if (stream.offset + 368 > chunkData.length) {
        console.log(`Buffer overflow at leaf ${i}: offset ${stream.offset}, buffer size ${chunkData.length}`);
        break;
      }

      // m_svoBounds (RCBox - 48 bytes: 6 x double)
      const svoBounds = [
      stream.getFloat64(), stream.getFloat64(), stream.getFloat64(),
      stream.getFloat64(), stream.getFloat64(), stream.getFloat64()];

      // m_nodeBounds (RCBox - 48 bytes: 6 x double)
      const nodeBounds = [
      stream.getFloat64(), stream.getFloat64(), stream.getFloat64(),
      stream.getFloat64(), stream.getFloat64(), stream.getFloat64()];


      // m_maxDepth (4 bytes: int32)
      const maxDepth = stream.getInt32();
      // m_amountOfPoints (4 bytes: int32)
      const amountOfPoints = stream.getInt32();
      // m_fileSize (4 bytes: int32)
      const fileSize = stream.getInt32();

      // m_numNonLeafNodes[32] (128 bytes: 32 x int32)
      /** @type {number[]} */
      const numNonLeafNodes = [];
      for (let j = 0; j < 32; j++) numNonLeafNodes.push(stream.getInt32());

      // m_numLeafNodes[32] (128 bytes: 32 x int32) — SVO leaf point counts per level
      /** @type {number[]} */
      const numSvoLeafNodes = [];
      for (let j = 0; j < 32; j++) numSvoLeafNodes.push(stream.getInt32());

      // Skip padding dummy[4] (4 bytes)
      stream.skip(4);

      leafNodes.push({
        index: i,
        svoBounds, nodeBounds,
        maxDepth, amountOfPoints, fileSize,
        numNonLeafNodes, numSvoLeafNodes
      });
    }

    return leafNodes;
  }

  /**
   * Parse umbrella extended data (chunk 3).
   * @param {Uint8Array} chunkData - Raw chunk bytes
   * @returns {UmbrellaExtendedData}
   */
  static parseUmbrellaExtendedData(chunkData) {
    const stream = new InputStream(chunkData);

    // Read levelNodeCnt[32] (128 bytes: 32 x int32)
    /** @type {number[]} */
    const levelNodeCounts = [];
    for (let i = 0; i < 32; i++) levelNodeCounts.push(stream.getInt32());

    // Calculate total number of intermediate nodes
    const totalNodes = levelNodeCounts.reduce((sum, count) => sum + count, 0);
    const nodeLevelStartIndices = [0];
    for (let i = 0; i < levelNodeCounts.length; i++) {
      nodeLevelStartIndices.push(nodeLevelStartIndices[i] + levelNodeCounts[i]);
    }

    console.log(`Reading ${totalNodes} umbrella intermediate nodes...`);

    /** @type {UmbrellaIntermediateNode[]} */
    const intermediateNodes = [];

    for (let i = 0; i < totalNodes; i++) {
      // m_rgba (4 bytes: RCVector4ub)
      const rgba = [stream.getUint8(), stream.getUint8(), stream.getUint8(), stream.getUint8()];
      // m_normalIndex (2 bytes: uint16)
      const normalIndex = stream.getUint16();
      // m_childInfo (2 bytes: uint16)
      const childInfo = stream.getUint16();

      // m_svoBounds (48 bytes: RCBox = 6 x double)
      const svoBounds = [
      stream.getFloat64(), stream.getFloat64(), stream.getFloat64(),
      stream.getFloat64(), stream.getFloat64(), stream.getFloat64()];

      // m_misc[4] (4 bytes: uint8 flags)
      const misc = [stream.getUint8(), stream.getUint8(), stream.getUint8(), stream.getUint8()];
      // m_indexIntoLeafArray (4 bytes: int32)
      const indexIntoLeafArray = stream.getInt32();
      // m_firstChildIndex (4 bytes: int32)
      const firstChildIndex = stream.getInt32();
      // Skip padding dummy[4] (4 bytes)
      stream.skip(4);

      intermediateNodes.push({
        index: i, rgba, normalIndex, childInfo,
        svoBounds, misc, indexIntoLeafArray, firstChildIndex
      });
    }

    return { levelNodeCounts, intermediateNodes, nodeLevelStartIndices };
  }

  /**
   * Reconstruct umbrella extended data (chunk 3) from the file header SVO bounds
   * and umbrella leaf summaries (chunk 2).
   *
   * The load path computes this instead of reading chunk 3 from disk because
   * that chunk might be corrupted in some RCS files. Additionally, this reduces
   * the data we need to download.
   *
   * Spatial hierarchy fields (bounds, child mask, first-child index, leaf index)
   * are fully recoverable. Per-node colour/normal/misc summaries are zeroed
   * because they require aggregating point data during indexing.
   *
   * @param {number[]} svoBounds - Umbrella root cube from the file header
   * @param {UmbrellaLeafNode[]} leafNodes - Parsed umbrella leaf nodes (chunk 2)
   * @returns {UmbrellaExtendedData}
   */
  static computeUmbrellaExtendedData(svoBounds, leafNodes) {
    // ── Octree helpers (same child ordering as the on-disk umbrella tree) ──

    /** @param {number[]} box */
    const boxCenter = (box) => [
    (box[0] + box[3]) / 2,
    (box[1] + box[4]) / 2,
    (box[2] + box[5]) / 2];


    /** @param {number[]} parentBox @param {number} childId */
    const childBounds = (parentBox, childId) => {
      const mid = boxCenter(parentBox);
      const outMin = [parentBox[0], parentBox[1], parentBox[2]];
      const outMax = [parentBox[3], parentBox[4], parentBox[5]];
      for (let axis = 0; axis < 3; axis++) {
        if (childId >> axis & 1) {
          outMin[axis] = mid[axis];
        } else {
          outMax[axis] = mid[axis];
        }
      }
      return [...outMin, ...outMax];
    };

    /** @param {number[]} parentBox @param {number} x @param {number} y @param {number} z */
    const childIdForPoint = (parentBox, x, y, z) => {
      const mid = boxCenter(parentBox);
      let id = 0;
      if (x >= mid[0]) id |= 1;
      if (y >= mid[1]) id |= 2;
      if (z >= mid[2]) id |= 4;
      return id;
    };

    /**
     * @typedef {Object} UmbrellaTreeNode
     * @property {number[]} bounds
     * @property {(UmbrellaTreeNode|null)[]|null} children
     * @property {number} leafIndex
     * @property {number} childMask
     * @property {number} firstChildIndex
     */

    /** @param {number[]} bounds @returns {UmbrellaTreeNode} */
    const newNode = (bounds) => ({
      bounds,
      children: null,
      leafIndex: -1,
      childMask: 0,
      firstChildIndex: -1
    });

    // Phase 1: insert each umbrella leaf (chunk 2 entry) at its computed depth,
    // creating ancestor nodes along the way.
    const root = newNode([...svoBounds]);
    const rootSize = svoBounds[3] - svoBounds[0];

    for (let i = 0; i < leafNodes.length; i++) {
      const lb = leafNodes[i].svoBounds;
      const leafSize = lb[3] - lb[0];
      const depth = leafSize > 0 ? Math.round(Math.log2(rootSize / leafSize)) : 0;
      const cx = (lb[0] + lb[3]) / 2;
      const cy = (lb[1] + lb[4]) / 2;
      const cz = (lb[2] + lb[5]) / 2;

      let node = root;
      for (let d = 0; d < depth; d++) {
        if (!node.children) {
          node.children = new Array(8).fill(null);
        }
        const cid = childIdForPoint(node.bounds, cx, cy, cz);
        let childNode = node.children[cid];
        if (!childNode) {
          childNode = newNode(childBounds(node.bounds, cid));
          node.children[cid] = childNode;
        }
        node = childNode;
      }
      node.leafIndex = i;
    }

    // Phase 2: breadth-first flatten and emit. Chunk 3 stores nodes level-by-level;
    // each interior node records an 8-bit child mask and the index of its first child
    // within the next level's contiguous node run. Colour/normal/misc summaries stay
    // zeroed — they need point aggregation at index time.

    /** @type {number[]} */
    const levelNodeCounts = new Array(RCS_MAX_OCTREE_LEVELS).fill(0);
    /** @type {UmbrellaIntermediateNode[]} */
    const intermediateNodes = [];
    let globalIndex = 0;

    /** @type {UmbrellaTreeNode[]} */
    let currentLevel = [root];

    for (let L = 0; L < RCS_MAX_OCTREE_LEVELS; L++) {
      if (currentLevel.length === 0) break;

      levelNodeCounts[L] = currentLevel.length;

      /** @type {UmbrellaTreeNode[]} */
      const nextLevelNodes = [];

      for (const parent of currentLevel) {
        if (parent.children && parent.leafIndex < 0) {
          parent.firstChildIndex = nextLevelNodes.length;
          for (let cid = 0; cid < 8; cid++) {
            const childNode = parent.children[cid];
            if (childNode) {
              parent.childMask |= 1 << cid;
              nextLevelNodes.push(childNode);
            }
          }
        }

        intermediateNodes.push({
          index: globalIndex++,
          rgba: [0, 0, 0, 0],
          normalIndex: 0,
          childInfo: parent.childMask,
          svoBounds: [...parent.bounds],
          misc: [0, 0, 0, 0],
          indexIntoLeafArray: parent.leafIndex,
          firstChildIndex: parent.firstChildIndex
        });
      }

      currentLevel = nextLevelNodes;
    }

    const nodeLevelStartIndices = [0];
    for (let i = 0; i < levelNodeCounts.length; i++) {
      nodeLevelStartIndices.push(nodeLevelStartIndices[i] + levelNodeCounts[i]);
    }

    return { levelNodeCounts, intermediateNodes, nodeLevelStartIndices };
  }

  /**
   * Decode SVO points split by octree level for hierarchical LOD distribution.
   *
   * @param {Uint8Array} buffer - Raw SVO payload buffer
   * @param {UmbrellaLeafNode} leafNode - Umbrella leaf metadata
   * @param {number} lodLevel - Target LOD level index for this fragment
   * @param {boolean} [interiorNodesIncluded] - Whether `buffer` starts with interior nodes
   * @returns {SvoByLevelResult}
   */
  static decodeSvoPointsByLevel(buffer, leafNode, lodLevel, interiorNodesIncluded, levelsInLod0 = MIN_LEVELS_IN_LOD0) {
    const result = this.decodeSvoPointsDirect(buffer, leafNode, lodLevel, interiorNodesIncluded, levelsInLod0);

    if (!result || !result.interleavedData || result.count === 0) {
      return { pointsByLevel: [], transformData: result ? result.transformData : null };
    }

    /** @type {SvoLevelEntry[]} */
    const pointsByLevel = [];
    const totalInterleaved = result.interleavedData;
    let currentPointIndex = 0;

    // Split the packed survivor buffer back into levels. Note that there
    // are less pointsPerLevel as the raw leafNode.numSvoLeafNodes count suggests
    // as points marked with a deleteFlag are excluded.
    const pointsPerLevel = result.pointsPerLevel;
    for (let level = 0; level < pointsPerLevel.length; level++) {
      const pointsInLevel = pointsPerLevel[level];
      if (pointsInLevel <= 0) continue;

      const sourceStart = currentPointIndex * 6;
      // slice() returns an independent Float32Array, safe to transfer to a worker.
      const levelData = totalInterleaved.slice(sourceStart, sourceStart + pointsInLevel * 6);

      pointsByLevel.push({
        interleavedData: levelData,
        count: pointsInLevel,
        level
      });

      currentPointIndex += pointsInLevel;
    }

    const bounds = leafNode.svoBounds;
    /** @type {SvoTransformData} */
    const transformData = {
      offsetX: bounds[0],
      offsetY: bounds[1],
      offsetZ: bounds[2]
    };

    return { pointsByLevel, transformData, bounds: result.bounds };
  }

  /**
   * High-performance SVO decoder that writes directly into an interleaved
   * Float32Array without intermediate objects or BigInt operations.
   *
   * @param {Uint8Array} buffer - Raw SVO payload buffer
   * @param {UmbrellaLeafNode} leafNode - Umbrella leaf metadata
   * @param {number} lodLevel - Target LOD level index for this fragment
   * @param {boolean} [interiorNodesIncluded=true] - Whether `buffer` starts with interior-node data
   * @param {number} [levelsInLod0] - Per-model LOD-0 source level count ('direct' mode only).
   * @returns {SvoDecodeResult}
   */
  static decodeSvoPointsDirect(buffer, leafNode, lodLevel, interiorNodesIncluded = true, levelsInLod0 = MIN_LEVELS_IN_LOD0) {
    // Calculate which levels to process
    const levelInfo = this._calculateLevelsToProcessForLod(leafNode, lodLevel, levelsInLod0);

    // If no levels to process, return empty result
    if (levelInfo.levelsToProcess.length === 0) {
      return {
        interleavedData: new Float32Array(0),
        count: 0,
        transformData: { offsetX: 0, offsetY: 0, offsetZ: 0 },
        bounds: leafNode.svoBounds,
        pointsPerLevel: [],
        leafNode
      };
    }

    // Count nodes
    const numInteriorNodes = leafNode.numNonLeafNodes.reduce((sum, count) => sum + count, 0);
    const numSvoLeafNodeTotal = leafNode.numSvoLeafNodes.reduce((sum, count) => sum + count, 0);

    // Calculate data offsets
    const nodeDataSize = (numInteriorNodes + numSvoLeafNodeTotal) * 4; // 4 bytes per node
    const leafDataStart = interiorNodesIncluded ? nodeDataSize : 0;

    const { leafStart, leafEnd } = levelInfo.nodeRanges;
    const leafsToProcess = leafEnd - leafStart;

    // Pre-allocate output buffer (6 floats per point: XYZ + RGB)
    // We'll process all leafs but skip deleted ones, so allocate max size
    const interleavedData = new Float32Array(leafsToProcess * 6);

    // Fixed scale factors
    const scaleX = 0.001;
    const scaleY = 0.001;
    const scaleZ = 0.001;
    const colorScale = 1.0 / 255.0;

    // DataView for efficient binary reading without BigInt
    const view = new DataView(buffer.buffer, buffer.byteOffset, buffer.byteLength);

    let outputIndex = 0;
    // interiorNodesIncluded=true : buffer starts at leaf-data byte 0 → skip to leafStart.
    // interiorNodesIncluded=false: buffer is pre-trimmed to leafStart → start at 0.
    const startOffset = leafDataStart + (interiorNodesIncluded ? leafStart * 16 : 0);

    // Overall point count per octree level
    const numSvoLeafNodes = leafNode.numSvoLeafNodes;
    // Surviving (non-deleted) point count per octree level
    const pointsPerLevel = new Array(numSvoLeafNodes.length).fill(0);

    let i = 0;
    for (const level of levelInfo.levelsToProcess) {
      // Add all non-deleted points in this level
      for (let n = 0; n < numSvoLeafNodes[level]; n++, i++) {
        const offset = startOffset + i * 16;

        // Read two 64-bit words as four 32-bit integers (little-endian)
        const w0_low = view.getUint32(offset, true);
        const w0_high = view.getUint32(offset + 4, true);
        const w1_low = view.getUint32(offset + 8, true);
        // w1_high not needed - only rgba in w1_low is used

        // Decode word0: x/y/z quantized offsets (18+18+18 bits), lidarClass (8), reserved (1), deleteFlag (1)
        const x = w0_low & 0x3FFFF; // Bits 0-17: x (18 bits)
        const y = (w0_low >>> 18 | (w0_high & 0xF) << 14) & 0x3FFFF; // Bits 18-35: y (18 bits)
        const z = w0_high >>> 4 & 0x3FFFF; // Bits 36-53: z (18 bits)
        const deleteFlag = w0_high >>> 31 & 0x1; // Bit 63: deleteFlag (1 bit)

        // Skip deleted points
        if (deleteFlag) continue;

        // Decode word1: rgba (32 bits), normalIndex (14), primitiveClass (4), reserved (14)
        const rgba = w1_low;
        // Extract RGBA components (order: ABGR in memory)
        const b = rgba >>> 8 & 0xFF;
        const g = rgba >>> 16 & 0xFF;
        const r = rgba >>> 24 & 0xFF;

        // Write directly to output buffer
        interleavedData[outputIndex++] = x * scaleX; // X
        interleavedData[outputIndex++] = y * scaleY; // Y
        interleavedData[outputIndex++] = z * scaleZ; // Z
        interleavedData[outputIndex++] = r * colorScale; // R
        interleavedData[outputIndex++] = g * colorScale; // G
        interleavedData[outputIndex++] = b * colorScale; // B

        pointsPerLevel[level]++;
      }
    }

    // Trim buffer to actual size (removing deleted points)
    const actualPointCount = outputIndex / 6;
    const trimmedData = actualPointCount < leafsToProcess ?
    new Float32Array(interleavedData.buffer, 0, outputIndex) :
    interleavedData;

    const bounds = leafNode.svoBounds;
    /** @type {SvoTransformData} */
    const transformData = {
      offsetX: bounds[0],
      offsetY: bounds[1],
      offsetZ: bounds[2],
      isIdentityRotation: true
    };

    return {
      interleavedData: trimmedData,
      count: actualPointCount,
      transformData,
      bounds,
      pointsPerLevel
    };
  }

  /**
   * Build the hierarchical octree structure from umbrella leaf data and
   * umbrella extended data (intermediate nodes).
   *
   * @param {UmbrellaLeafNode[]} leafNodes - Parsed umbrella leaf nodes (chunk 2)
   * @param {UmbrellaExtendedData} extendedData - Parsed umbrella extended data (chunk 3)
   * @returns {RcsOctreeStructure}
   */
  static buildOctreeStructure(leafNodes, extendedData) {
    const { intermediateNodes, nodeLevelStartIndices } = extendedData;
    /** @type {OctreeNode[]} */
    const nodes = [];
    let nextNodeId = 0;
    /** @type {Set<number>} */
    const visitedNodes = new Set();

    function buildTreeIteratively() {
      if (intermediateNodes.length === 0) return;

      /** @type {Array<{nodeIndexInFile: number, nodeId: number, parentId: number|null, nodeLevel: number}>} */
      const queue = [{ nodeIndexInFile: 0, nodeId: nextNodeId++, parentId: null, nodeLevel: 0 }];

      let queueHead = 0;
      while (queueHead < queue.length) {
        const { nodeIndexInFile, nodeId, nodeLevel } = queue[queueHead++];

        if (nodeIndexInFile < 0 || nodeIndexInFile >= intermediateNodes.length) continue;
        if (visitedNodes.has(nodeIndexInFile)) {
          console.warn(`Circular reference detected at intermediate node ${nodeIndexInFile}`);
          continue;
        }
        visitedNodes.add(nodeIndexInFile);

        const intermediate = intermediateNodes[nodeIndexInFile];

        let indexIntoLeafArray = intermediate.indexIntoLeafArray;
        if (indexIntoLeafArray < 0 || indexIntoLeafArray >= leafNodes.length) {
          indexIntoLeafArray = -1;
        }

        const leafNode = indexIntoLeafArray !== -1 ? leafNodes[indexIntoLeafArray] : null;

        /** @type {OctreeNode} */
        let node;
        if (leafNode) {
          console.assert(intermediate.firstChildIndex === -1);
          // Leaf node: owns SVO data and has no children.
          node = {
            id: nodeId,
            boundingBox: intermediate.svoBounds,
            children: [],
            isLeaf: true,
            fileOffsets: {
              svoDataSize: leafNode.fileSize,
              leafIndex: indexIntoLeafArray
            },
            numSvoLeafNodes: leafNode.numSvoLeafNodes,
            numNonLeafNodes: leafNode.numNonLeafNodes,
            fragmentId: indexIntoLeafArray, // Direct mapping
            nodeBounds: leafNode.nodeBounds
          };
        } else {
          // Interior node: queue its children. A node that references a leaf
          // index but has no leaf data also lands here, as a childless interior
          // node (matching the previous behaviour).
          const children = [];
          if (indexIntoLeafArray === -1) {
            const childMask = getChildrenFromMask(intermediate.childInfo);
            if (childMask.length > 0 && intermediate.firstChildIndex >= 0) {
              for (let i = 0; i < childMask.length; i++) {
                const childIndex = nodeLevelStartIndices[nodeLevel + 1] + intermediate.firstChildIndex + i;
                if (childIndex >= 0 && childIndex < intermediateNodes.length) {
                  const childNodeId = nextNodeId++;
                  children.push(childNodeId);
                  queue.push({
                    nodeIndexInFile: childIndex,
                    nodeId: childNodeId,
                    parentId: nodeId,
                    nodeLevel: nodeLevel + 1
                  });
                }
              }
            }
          }
          node = {
            id: nodeId,
            boundingBox: intermediate.svoBounds,
            children,
            isLeaf: false,
            fileOffsets: {}
          };
        }

        nodes.push(node);
      }
    }

    // Note: after the build nodes is sorted in ascending order or element.id, root is first.
    buildTreeIteratively();

    console.log(`Built octree with ${nodes.length} nodes, visited ${visitedNodes.size} intermediate nodes`);

    return { nodes };
  }

  /**
   * Map a numeric chunk ID to a human-readable name.
   * @param {number} chunkId
   * @returns {string}
   */
  static getChunkName(chunkId) {
    /** @type {Record<number, string>} */
    const chunkNames = {
      1: 'Voxel data (SVO interior + leaf points)',
      2: 'Umbrella octree data (leaf summaries)',
      3: 'Umbrella extended (hierarchy details)',
      4: 'Range image (PNG)',
      5: 'Preview image (JPEG/PNG)',
      6: 'Segment info',
      7: 'Voxel segment data',
      8: 'Segment image (PNG)',
      9: 'RGB image data (JPEG)',
      10: 'E57 properties',
      11: 'Other info (global metadata)',
      12: 'Occlusion info',
      13: 'Time stamps'
    };
    return chunkNames[chunkId] || `Unknown chunk ${chunkId}`;
  }

  /**
   * Calculate the interior-node and leaf-node index ranges for a LOD level.
   *
   * @private
   * @param {UmbrellaLeafNode} leafNode - Umbrella leaf metadata
   * @param {number} lodLevel - Target LOD level index
   * @param {number} [levelsInLod0] - Per-model LOD-0 source level count ('direct' mode only).
   * @returns {LevelProcessingInfo}
   */
  static _calculateLevelsToProcessForLod(leafNode, lodLevel, levelsInLod0 = MIN_LEVELS_IN_LOD0) {
    const numNonLeafNodes = leafNode.numNonLeafNodes;
    const numSvoLeafNodes = leafNode.numSvoLeafNodes;

    const { levelsToProcess, totalPoints } =
    calculateLevelsToProcessForLod(numSvoLeafNodes, lodLevel, levelsInLod0);

    const lastIncludedLevel = levelsToProcess.length > 0 ?
    levelsToProcess[levelsToProcess.length - 1] :
    -1;

    const nodeRanges = this._calculateNodeRanges(numNonLeafNodes, numSvoLeafNodes, levelsToProcess);

    return {
      levelsToProcess,
      lastIncludedLevel,
      estimatedPoints: totalPoints,
      nodeRanges
    };
  }

  /**
   * Compute start/end indices for interior nodes and leaf nodes given a
   * set of octree levels.
   *
   * @private
   * @param {number[]} numNonLeafNodes - Interior node counts per level
   * @param {number[]} numSvoLeafNodes - SVO leaf point counts per level
   * @param {number[]} levelsToProcess - Which levels to include
   * @returns {SvoNodeRanges}
   */
  static _calculateNodeRanges(numNonLeafNodes, numSvoLeafNodes, levelsToProcess) {
    if (levelsToProcess.length === 0) {
      return { nodeStart: 0, nodeEnd: 0, leafStart: 0, leafEnd: 0 };
    }

    const minLevel = Math.min(...levelsToProcess);
    const maxLevel = Math.max(...levelsToProcess);

    // Calculate interior node ranges
    let nodeStart = 0;
    let nodeEnd = 0;
    for (let level = 0; level <= maxLevel; level++) {
      if (level < minLevel) nodeStart += numNonLeafNodes[level] || 0;
      nodeEnd += numNonLeafNodes[level] || 0;
    }

    // Calculate leaf node ranges
    let leafStart = 0;
    let leafEnd = 0;
    for (let level = 0; level <= maxLevel; level++) {
      if (level < minLevel) leafStart += numSvoLeafNodes[level] || 0;
      if (level <= maxLevel) leafEnd += numSvoLeafNodes[level] || 0;
    }

    return { nodeStart, nodeEnd, leafStart, leafEnd, minLevel, maxLevel };
  }
}